import { useState, useCallback } from "react";
import { SlidersHorizontal } from "lucide-react";
import { Card } from "@/components/ui/card";
import { IterationControls, type IterationData } from "@/components/chat/IterationControls";

interface IterationWidgetProps {
  data: Record<string, unknown>;
  onAction?: (actionId: string, context?: Record<string, unknown>) => void;
}

/**
 * Standalone iteration widget — triggered when user asks to adjust
 * a parameter without specifying a value. Shows the current value
 * and lets them tweak it directly.
 */
export function IterationWidget({ data, onAction }: IterationWidgetProps) {
  const iterData = data as unknown as IterationData;
  const [showPush, setShowPush] = useState(false);
  const [applying, setApplying] = useState(false);
  const [pushing, setPushing] = useState(false);
  const [currentData, setCurrentData] = useState(iterData);

  const handleApply = useCallback((newValue: string, _updatedData: IterationData) => {
    if (!onAction) return;
    setApplying(true);
    onAction("iteration_apply", {
      __action: "iteration_apply",
      __payload: {
        iteration_data: currentData,
        new_value: newValue,
      },
    });
    // Optimistically update — backend will send iteration_applied
    setTimeout(() => {
      setCurrentData((prev) => ({ ...prev, current_value: newValue }));
      setApplying(false);
      setShowPush(true);
    }, 500);
  }, [onAction, currentData]);

  const handlePush = useCallback(() => {
    if (!onAction) return;
    setPushing(true);
    onAction("iteration_push", {
      __action: "iteration_push",
      __payload: {},
    });
    setTimeout(() => setPushing(false), 3000);
  }, [onAction]);

  return (
    <Card variant="default" padding="sm" className="max-w-md animate-slide-up">
      <div className="flex items-center gap-2 pb-2.5 border-b border-daydra-border">
        <SlidersHorizontal size={14} className="text-daydra-accent shrink-0" />
        <span className="text-sm font-medium text-daydra-text">
          Adjust {currentData.param_name}
        </span>
        <span className="text-2xs text-daydra-muted ml-auto font-mono">
          {currentData.file_path.split("/").pop()}
        </span>
      </div>
      <div className="pt-2.5">
        <IterationControls
          data={currentData}
          onApply={handleApply}
          onPush={handlePush}
          showPush={showPush}
          applying={applying}
          pushing={pushing}
        />
      </div>
    </Card>
  );
}
